import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Trophy, Laptop } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import keishIcon from "@/assets/icon-green.svg";
import keishIconWhite from "@/assets/icon-white.svg";
import ContactSheet from "@/components/ContactSheet";
import { useTheme } from "@/components/ThemeProvider";
import MoreWorkCarousel from "@/components/MoreWorkCarousel";
import SEO from "@/components/SEO";

interface ContentSection {
  heading: string;
  body: string;
  images: Array<{ url: string; caption: string }>;
}

interface CaseStudy {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  category: string | null;
  cover_image_url: string | null;
  logo_url: string | null;
  content_sections: ContentSection[];
  content_markdown: string | null;
  images: Array<{ url: string; caption: string }>;
  tags: string[];
  live_url: string | null;
  likes: number;
  views: number;
}

/** Junk section headings/body patterns from Contra scraping */
const isJunkSection = (section: ContentSection): boolean => {
  const h = section.heading.toLowerCase();
  const b = section.body.toLowerCase();
  const junkHeadings = [
    'check out the live site', 'buy a vinyl', 'reviews:', 'preview the site',
    'save a seat', 'hours', 'private events', 'about us', 'food and drink',
    'brooklyn, nyc', 'from the client',
  ];
  if (junkHeadings.some(j => h.includes(j))) return true;
  if (b.includes('madison green') || b.includes('completed work') || b.includes('timeline')) return true;
  if (/^\d+(\.\d+)?[KkMm]?\s*$/.test(b.trim())) return true;
  // Sections whose body is just a heading marker like "#### +"
  if (/^#{1,6}\s*\+?\s*$/.test(b.trim())) return true;
  // Sections with purchase/gumroad links or standalone "Menu" text — Contra scraping artifacts
  if (b.includes('gumroad.com') || b.includes('purchase template')) return true;
  // Headings that are markdown links to the live site (e.g., "[MĒR New York](https://mer.framer.website/)")
  if (/^\[.+\]\(https?:\/\//.test(section.heading.trim())) return true;
  // Sections with only tiny avatar/thumbnail images and no real content
  const hasOnlyThumbs = section.images.length > 0 && section.images.every(
    img => img.url.includes('w_200') || img.url.includes('w_400') || img.url.includes('w_100')
  );
  if (hasOnlyThumbs && b.length < 50) return true;
  return false;
};

/** Strip lines from body that duplicate image captions or contain Contra artifacts */
const cleanSectionBody = (body: string, images: Array<{ url: string; caption: string }>): string => {
  const captions = new Set(images.map(img => img.caption?.trim()).filter(Boolean));

  return body
    .split('\n')
    .filter(line => {
      const trimmed = line.trim();
      // Remove lines that duplicate image captions
      if (captions.size && captions.has(trimmed)) return false;
      // Remove HTML5 video fallback text
      if (trimmed.startsWith('Your browser does not support HTML video')) return false;
      // Remove "Here is a link to the video" standalone lines
      if (/^Here is \[a link to the video\]/.test(trimmed)) return false;
      // Remove standalone "instead." left from split video fallback
      if (trimmed === 'instead.') return false;
      // Remove video-adjacent caption lines (short lines right after video fallback removal)
      if (trimmed === 'Value highlight with a snapshot of Oscar\'s work') return false;
      // Remove "What the client had to say" lines
      if (trimmed === 'What the client had to say') return false;
      // Remove meta lines: standalone view/like counts
      if (/^\d[\d,.]*[KkMm]?\s*$/.test(trimmed)) return false;
      // Remove timeline/client meta
      if (/^Timeline$/i.test(trimmed)) return false;
      if (/^Clients?$/i.test(trimmed)) return false;
      // Remove date range lines like "Dec 20, 2024 - Jan 5, 2025"
      if (/^[A-Z][a-z]{2}\s+\d{1,2},\s*\d{4}\s*-\s*[A-Z][a-z]{2}\s+\d{1,2},\s*\d{4}$/.test(trimmed)) return false;
      return true;
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/** Clean markdown → HTML for database content */
const renderMarkdown = (md: string): string => {
  const placeholders: string[] = [];
  const ph = (html: string) => { placeholders.push(html); return `\x00PH${placeholders.length - 1}\x00`; };

  const inlineLink = (href: string, text: string) =>
    ph(`<a href="${href}" target="_blank" rel="noopener noreferrer" class="cs-inline-link">${text}</a>`);

  const bookmark = (url: string, label?: string) => {
    let domain: string;
    try { domain = new URL(url).hostname.replace(/^www\./, ""); } catch { domain = url; }
    return ph(
      `<a href="${url}" target="_blank" rel="noopener noreferrer" class="cs-bookmark" data-bookmark-url="${url}">` +
        `<span class="cs-bookmark-inner">` +
          `<span class="cs-bookmark-meta">` +
            `<span class="cs-bookmark-title">${label || domain}</span>` +
            `<span class="cs-bookmark-domain">${domain}</span>` +
          `</span>` +
        `</span>` +
        `<span class="cs-bookmark-thumb" data-bookmark-thumb></span>` +
        `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 7h10v10"/><path d="M7 17 17 7"/></svg>` +
      `</a>`
    );
  };

  const isUrl = (t: string) => /^https?:\/\//.test(t.trim());

  // --- Extract fenced code blocks first so inner content isn't processed ---
  let work = md.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
    const escaped = code.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return ph(`<pre class="cs-code-block"><code${lang ? ` class="language-${lang}"` : ""}>${escaped}</code></pre>`);
  });

  // Images: ![alt](url)
  work = work.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt, src) => {
    return ph(
      `<figure class="cs-figure">` +
        `<img src="${src}" alt="${alt || ''}" class="cs-img" loading="lazy" />` +
        (alt ? `<figcaption class="cs-figcaption">${alt}</figcaption>` : '') +
      `</figure>`
    );
  });

  // Inline code
  work = work.replace(/`([^`]+)`/g, (_, code) => {
    const escaped = code.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return ph(`<code class="cs-inline-code">${escaped}</code>`);
  });

  // Bold, italic
  work = work
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/_(.+?)_/g, "<em>$1</em>");

  // Markdown links: [text](url)
  // Decide inline-dotted vs bookmark based on whether the link is the only content on its line
  work = work.replace(/^(.*?)\[([^\]]+)\]\(([^)]+)\)(.*?)$/gm, (fullMatch, before, text, href, after) => {
    if (!isUrl(href)) return `${before}${inlineLink(href, text)}${after}`;
    const hasContext = before.trim().length > 0 || after.trim().length > 0;
    if (hasContext) {
      // Inline within a sentence → dotted underline
      const label = isUrl(text) ? undefined : text.replace(/\*+/g, "").trim();
      return `${before}${inlineLink(href, label || text)}${after}`;
    }
    // Standalone on its own line → bookmark card
    const label = isUrl(text) ? undefined : text.replace(/\*+/g, "").trim();
    return bookmark(href, label || undefined);
  });

  // Bare URLs not already inside a placeholder
  // If the URL is standalone on its line → bookmark, otherwise inline dotted link
  work = work.replace(/^(.*?)(https?:\/\/[^\s<\x00]+)(.*?)$/gm, (fullMatch, before, url, after) => {
    const hasContext = before.trim().length > 0 || after.trim().length > 0;
    if (hasContext) return `${before}${inlineLink(url, url)}${after}`;
    return bookmark(url);
  });

  // Restore placeholders
  work = work.replace(/\x00PH(\d+)\x00/g, (_, i) => placeholders[Number(i)]);

  // --- Process line by line ---
  const lines = work.split('\n');
  const processed: string[] = [];
  let listBuffer: string[] = [];
  let blockquoteBuffer: string[] = [];
  let pendingBlanks = 0;

  const flushList = () => {
    if (listBuffer.length > 0) {
      processed.push('<ul class="cs-list">' + listBuffer.map(l => `<li>${l}</li>`).join('') + '</ul>');
    }
    listBuffer = [];
  };

  const flushBlockquote = () => {
    if (blockquoteBuffer.length > 0) {
      processed.push(`<blockquote class="cs-blockquote">${blockquoteBuffer.join('<br/>')}</blockquote>`);
      blockquoteBuffer = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();

    // Horizontal rules
    if (/^(?:---+|\*\*\*+|___+)\s*$/.test(trimmed)) {
      flushList();
      flushBlockquote();
      processed.push('<hr class="cs-hr" />');
      continue;
    }

    // Headings (## – ######)
    const headingMatch = trimmed.match(/^(#{2,6})\s+(.+)$/);
    if (headingMatch) {
      flushList();
      flushBlockquote();
      const level = headingMatch[1].length;
      processed.push(`<h${level} class="cs-h${level}">${headingMatch[2]}</h${level}>`);
      continue;
    }

    // Blockquotes
    if (trimmed.startsWith('> ')) {
      flushList();
      blockquoteBuffer.push(trimmed.slice(2));
      continue;
    } else {
      flushBlockquote();
    }

    // Empty line — paragraph break
    if (!trimmed) {
      if (listBuffer.length > 0) {
        pendingBlanks++;
      }
      // Skip blank lines — spacing is handled by element margins
      continue;
    }

    // Bullet list markers (-, •, ·, *)
    const bulletMatch = trimmed.match(/^(?:[•·\-\*])\s+(.+)$/);
    if (bulletMatch) {
      listBuffer.push(bulletMatch[1]);
      pendingBlanks = 0;
      continue;
    }

    // HTML block elements break lists and are pushed raw; inline HTML (strong, em, a, code, span) gets wrapped in <p>
    if (trimmed.startsWith('<')) {
      const isBlockElement = /^<(figure|div|ul|ol|li|table|pre|blockquote|hr|h[1-6]|section|article|nav|aside|header|footer)\b/i.test(trimmed);
      flushList();
      pendingBlanks = 0;
      if (isBlockElement) {
        processed.push(line);
      } else {
        processed.push(`<p>${line}</p>`);
      }
      continue;
    }

    // Heuristic: short line, no period at end, not a full sentence → implicit list
    const isListLike = trimmed.length < 120 && !trimmed.endsWith('.') && !trimmed.endsWith(':') && !trimmed.startsWith('#');
    if (isListLike) {
      listBuffer.push(trimmed);
      pendingBlanks = 0;
    } else {
      flushList();
      pendingBlanks = 0;
      processed.push(`<p>${line}</p>`);
    }
  }
  flushList();
  flushBlockquote();

  return processed.join('\n');
};

const CaseStudyPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [study, setStudy] = useState<CaseStudy | null>(null);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState<{ name: string; company: string | null; testimonial: string } | null>(null);
  const { mode } = useTheme();
  const isDark = mode === "dark";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const fetchStudy = async () => {
      if (!slug) return;
      const { data, error } = await supabase
        .from("case_studies")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();

      if (!error && data) {
        setStudy({
          ...data,
          content_sections: (data.content_sections as unknown as ContentSection[]) || [],
          content_markdown: (data as any).content_markdown || null,
          images: (data.images as unknown as Array<{ url: string; caption: string }>) || [],
          tags: data.tags || [],
        });

        // Fetch matching client testimonial
        const title = data.title?.toLowerCase() || '';
        const { data: reviews } = await supabase.from("reviews").select("name, company, testimonial");
        if (reviews && reviews.length > 0) {
          const match = reviews.find(r => {
            // Extract company from "Name, Company" format or company field
            const comp = r.company?.toLowerCase() || '';
            const nameComp = r.name.split(',').slice(1).join(',').trim().toLowerCase();
            return (comp && title.includes(comp.replace(/,?\s*(llc|inc|ltd)\.?/gi, '').trim())) ||
                   (nameComp && title.includes(nameComp.replace(/,?\s*(llc|inc|ltd)\.?/gi, '').trim()));
          });
          if (match) setReview(match);
        }
      }
      setLoading(false);
    };
    fetchStudy();
  }, [slug]);
  // Enrich bookmark links with og:image after content renders
  useEffect(() => {
    if (!study) return;
    const bookmarks = document.querySelectorAll<HTMLAnchorElement>("[data-bookmark-url]");
    if (bookmarks.length === 0) return;

    const urls = Array.from(new Set(Array.from(bookmarks).map(el => el.dataset.bookmarkUrl!)));

    const enrich = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-og-meta`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
            body: JSON.stringify({ urls }),
          }
        );
        if (!res.ok) return;
        const results: Array<{ url: string; title: string | null; description: string | null; image: string | null }> = await res.json();
        const metaMap = new Map(results.map(r => [r.url, r]));

        bookmarks.forEach(el => {
          const url = el.dataset.bookmarkUrl!;
          const meta = metaMap.get(url);
          if (!meta) return;

          // Update title if we got one
          if (meta.title) {
            const titleEl = el.querySelector(".cs-bookmark-title");
            if (titleEl) titleEl.textContent = meta.title;
          }

          // Add og:image thumbnail
          if (meta.image) {
            const thumbEl = el.querySelector("[data-bookmark-thumb]");
            if (thumbEl) {
              const img = document.createElement("img");
              img.src = meta.image;
              img.alt = meta.title || "";
              img.className = "cs-bookmark-image";
              img.loading = "lazy";
              thumbEl.appendChild(img);
              el.classList.add("cs-bookmark--has-image");
            }
          }
        });
      } catch {
        // Silently fail — bookmarks still work without og data
      }
    };
    enrich();
  }, [study]);


  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="font-body text-ts-xs text-muted-foreground tracking-widest uppercase animate-pulse">
          Loading…
        </div>
      </div>
    );
  }

  if (!study) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="font-body text-ts-xs text-muted-foreground">Project not found.</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-body text-ts-2xs tracking-[0.15em] uppercase text-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft size={12} />
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={study.title}
        description={study.subtitle || `${study.title} — a case study by KEISH`}
        canonical={`/work/${study.slug}`}
        ogImage={study.cover_image_url || undefined}
        ogType="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          name: study.title,
          description: study.subtitle || undefined,
          image: study.cover_image_url || undefined,
          url: `https://keish.studio/work/${study.slug}`,
          creator: { "@type": "Organization", name: "KEISH" },
        }}
      />
      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 md:px-10 lg:px-14 py-4 backdrop-blur-xl border-b transition-colors duration-500"
        style={{
          backgroundColor: isDark ? "hsla(0, 0%, 9%, 0.9)" : "hsla(45, 100%, 98%, 0.9)",
          color: isDark ? "hsl(27, 61%, 92%)" : "hsl(0, 0%, 9%)",
          borderColor: isDark ? "hsla(27, 61%, 92%, 0.1)" : "hsl(45, 10%, 88%)",
        }}
      >
        <Link to="/" className="flex items-center gap-2 group">
          <ArrowLeft size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" />
          <img src={isDark ? keishIconWhite : keishIcon} alt="KEISH" className="w-6 h-6" />
          <span className="font-body text-ts-2xs font-semibold tracking-[0.2em] uppercase">KEISH</span>
        </Link>
        <div className="flex items-center gap-4">
          <ContactSheet>
            <button className="font-body text-ts-2xs tracking-[0.15em] uppercase opacity-50 hover:opacity-100 transition-opacity">
              Work With Us
            </button>
          </ContactSheet>
        </div>
      </header>

      {/* Hero */}
      <div className="pt-28 md:pt-36 pb-16 md:pb-24">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
          >
            {study.category && (
              <p className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground mb-3">
                {study.category}
              </p>
            )}
            <h1 className="font-display text-5xl md:text-7xl italic text-foreground mb-4">
              {study.title}
            </h1>
            {study.subtitle && (
              <p className="font-body text-ts-sm text-muted-foreground tracking-wide max-w-[560px] leading-relaxed">
                {study.subtitle}
              </p>
            )}

            {/* Tags + CTA row */}
            <div className="flex flex-wrap items-center gap-3 mt-8">
              {study.live_url && (
                <a
                  href={study.live_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-body text-ts-2xs font-medium tracking-[0.15em] uppercase transition-colors hover:bg-primary/90"
                >
                  View Live Site
                  <ArrowUpRight size={12} />
                </a>
              )}
            </div>

            {study.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">
                {study.tags.map((tag) => {
                  const lower = tag.toLowerCase();
                  const isAward = /best|top|hire|award|winner/i.test(lower);
                  const isRole = /designer|developer|dev\b/i.test(lower);
                  return (
                    <span
                      key={tag}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 font-body text-ts-2xs tracking-[0.1em] uppercase ${
                        isAward
                          ? "bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400"
                          : "border border-border text-muted-foreground"
                      }`}
                    >
                      {isAward && <Trophy size={10} className="shrink-0" />}
                      {!isAward && isRole && <Laptop size={10} className="shrink-0" />}
                      {isAward ? `awarded: ${tag}` : tag}
                    </span>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>
      </div>


      {/* Overview */}



      {/* Content: markdown override OR structured sections */}
      {study.content_markdown ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="max-w-[700px] mx-auto px-5 md:px-10 pb-8"
        >
          <div
            className="font-body text-ts-xs leading-[1.9] text-foreground/75 [&_p]:mb-[0.75em] [&_a:not(.cs-bookmark)]:text-primary [&_a:not(.cs-bookmark)]:underline [&_a:not(.cs-bookmark)]:decoration-dotted [&_a:not(.cs-bookmark)]:underline-offset-4 [&_a:not(.cs-bookmark)]:inline-flex [&_a:not(.cs-bookmark)]:items-center [&_a:not(.cs-bookmark)]:gap-0.5 [&_a_svg]:inline-block [&_strong]:text-foreground [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: renderMarkdown((() => {
              let md = study.content_markdown!;
              if (study.live_url) {
                // Check if markdown already contains the live site domain
                try {
                  const domain = new URL(study.live_url).hostname.replace(/^www\./, '');
                  if (!md.includes(domain)) {
                    md += `\n\n---\n\n## Check out the live site ↗\n\n[${study.title}](${study.live_url})`;
                  }
                } catch {
                  // fallback: exact match
                  if (!md.includes(study.live_url)) {
                    md += `\n\n---\n\n## Check out the live site ↗\n\n[${study.title}](${study.live_url})`;
                  }
                }
              }
              return md;
            })()) }}
          />
        </motion.div>
      ) : (
        study.content_sections
          .filter(section => !isJunkSection(section))
          .filter(section => {
            const validImages = section.images.filter(
              img => !img.url.includes('w_200') && !img.url.includes('w_400') && !img.url.includes('w_100')
            );
            return section.body.trim().length > 0 || validImages.length > 0;
          })
          .map((section, i) => {
            const cleanedBody = cleanSectionBody(section.body, section.images);
            const validImages = section.images.filter(
              img => !img.url.includes('w_200') && !img.url.includes('w_400') && !img.url.includes('w_100')
            );
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5 }}
                className="max-w-[700px] mx-auto px-5 md:px-10 pb-16"
              >
                {(() => {
                  const rawHeading = section.heading.replace(/^\d+\\?\.\s*/, "");
                  // Parse markdown links in headings: [text](url) → just text
                  const cleanHeading = rawHeading.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
                  return (
                    <h2 className="font-display text-2xl md:text-3xl italic text-foreground mb-4">
                      {cleanHeading}
                    </h2>
                  );
                })()}
                {validImages.length > 0 && (
                  <div className="mt-4 space-y-6">
                    {validImages.map((img, j) => (
                      <figure key={j} className="cs-figure">
                        <img
                          src={img.url}
                          alt={img.caption || section.heading}
                          className="cs-img"
                          loading="lazy"
                        />
                        {img.caption && (
                          <figcaption className="cs-figcaption">
                            {img.caption}
                          </figcaption>
                        )}
                      </figure>
                    ))}
                  </div>
                )}

                {(() => {
                  // Extract URLs from the heading (markdown links) and prepend as bookmarks
                  const headingUrls: string[] = [];
                  const rawH = section.heading;
                  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
                  let m;
                  while ((m = linkRe.exec(rawH)) !== null) {
                    if (/^https?:\/\//.test(m[2])) headingUrls.push(m[2]);
                  }
                  const headingBookmarks = headingUrls.length > 0
                    ? headingUrls.map(u => `[${u}](${u})`).join("\n") + "\n\n"
                    : "";
                  const fullBody = headingBookmarks + (cleanedBody || "");
                  if (!fullBody.trim()) return null;
                  return (
                    <div
                      className="font-body text-ts-xs leading-[1.9] text-foreground/75 [&_p]:mb-[0.75em] [&_a:not(.cs-bookmark)]:text-primary [&_a:not(.cs-bookmark)]:underline [&_a:not(.cs-bookmark)]:decoration-dotted [&_a:not(.cs-bookmark)]:underline-offset-4 [&_a:not(.cs-bookmark)]:inline-flex [&_a:not(.cs-bookmark)]:items-center [&_a:not(.cs-bookmark)]:gap-0.5 [&_a_svg]:inline-block [&_strong]:text-foreground [&_strong]:font-semibold mt-6"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(fullBody) }}
                    />
                  );
                })()}
              </motion.div>
            );
          })
      )}

      {/* Live site bookmark for structured (non-markdown) case studies */}
      {!study.content_markdown && study.live_url && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="max-w-[700px] mx-auto px-5 md:px-10 pb-8"
        >
          <div
            className="font-body text-ts-xs leading-[1.9] text-foreground/75 [&_a:not(.cs-bookmark)]:text-primary [&_strong]:text-foreground [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(`---\n\n## Check out the live site ↗\n\n[${study.title}](${study.live_url})`) }}
          />
        </motion.div>
      )}

      {/* Client Testimonial */}
      {review && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="max-w-[700px] mx-auto px-5 md:px-10 py-12 md:py-16"
        >
          <hr className="cs-hr mb-8" />
          <div className="relative pl-6 md:pl-8">
            <span className="absolute left-0 top-0 font-display text-4xl md:text-5xl italic text-primary/30 leading-none select-none">"</span>
            <blockquote className="font-display text-lg md:text-xl italic text-foreground/90 leading-relaxed mb-4">
              {review.testimonial}
            </blockquote>
            <p className="font-body text-ts-2xs tracking-[0.15em] uppercase text-muted-foreground">
              {review.name.split(',')[0].trim()}
              {(review.company || review.name.includes(',')) && (
                <span className="text-muted-foreground/50">
                  {' · '}
                  {review.company || review.name.split(',').slice(1).join(',').trim()}
                </span>
              )}
            </p>
          </div>
        </motion.div>
      )}

      {/* More Work Carousel */}
      {slug && <MoreWorkCarousel currentSlug={slug} minCards={4} />}

      <div className="section-dark py-16 px-5 md:px-10">
        <div className="max-w-[800px] mx-auto text-center">
          <p className="font-body text-ts-2xs tracking-[0.25em] uppercase text-white/40 mb-3">
            Interested in working together?
          </p>
          <h3 className="font-display text-3xl md:text-4xl italic text-white/90 mb-8">
            Let's make something good.
          </h3>
          <div className="flex items-center justify-center gap-4">
            <ContactSheet>
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-white/90 text-[hsl(0,0%,9%)] font-body text-ts-2xs font-medium tracking-[0.15em] uppercase hover:bg-white transition-colors">
                Start a Project
                <ArrowUpRight size={12} />
              </button>
            </ContactSheet>
          </div>
        </div>
      </div>

      {/* Awwwards badge — Empire only */}
      {slug === "empire" && (
        <div style={{ position: "fixed", zIndex: 999, transform: "translateY(-50%)", top: "50%", right: 0 }}>
          <a href="https://www.awwwards.com/sites/empire" target="_blank" rel="noopener noreferrer">
            <svg width="53.08" height="171.358">
              <path fill="white" d="M0 0h53.08v171.358H0z" />
              <g fill="black">
                <path d="M20.048 153.585v-2.002l6.752-3.757h-6.752v-1.9h10.23v2.002l-6.752 3.757h6.752v1.9zM29.899 142.382a3.317 3.317 0 0 1-1.359 1.293c-.575.297-1.223.446-1.944.446-.721 0-1.369-.149-1.944-.446a3.317 3.317 0 0 1-1.359-1.293c-.331-.564-.497-1.232-.497-2.003 0-.769.166-1.437.497-2.002a3.332 3.332 0 0 1 1.359-1.294c.575-.297 1.224-.445 1.944-.445.722 0 1.369.148 1.944.445a3.326 3.326 0 0 1 1.359 1.294c.33.565.496 1.233.496 2.002.001.77-.166 1.438-.496 2.003m-1.703-3.348c-.435-.331-.967-.497-1.601-.497s-1.167.166-1.601.497c-.434.332-.65.78-.65 1.345s.217 1.014.65 1.346c.434.33.967.496 1.601.496s1.166-.166 1.601-.496c.434-.332.649-.78.649-1.346.001-.565-.215-1.013-.649-1.345M22.912 134.996v-1.812h1.185c-.43-.283-.752-.593-.973-.929-.219-.336-.329-.732-.329-1.19 0-.479.127-.902.38-1.272.254-.37.635-.633 1.141-.79-.478-.262-.851-.591-1.118-.985a2.221 2.221 0 0 1-.402-1.265c0-.682.2-1.218.599-1.607.4-.391.957-.585 1.668-.585h5.218v1.812H25.37c-.682 0-1.023.303-1.023.907 0 .467.264.85.789 1.146.527.299 1.286.446 2.28.446h2.865v1.813H25.37c-.682 0-1.023.303-1.023.906 0 .468.275.851.826 1.147.551.298 1.352.446 2.404.446h2.704v1.812h-7.369zM21.626 122.457c-.225.224-.502.336-.833.336s-.608-.112-.833-.336a1.128 1.128 0 0 1-.336-.833c0-.331.111-.609.336-.833.225-.225.502-.336.833-.336s.608.111.833.336c.225.224.337.502.337.833 0 .332-.112.608-.337.833m1.286-1.739h7.366v1.813h-7.366v-1.813zM22.912 118.668v-1.812h1.185a3.348 3.348 0 0 1-.951-1.009 2.434 2.434 0 0 1-.351-1.272c0-.681.19-1.229.57-1.644.38-.414.931-.621 1.651-.621h5.263v1.812h-4.722c-.418 0-.727.096-.92.285-.195.19-.293.447-.293.769 0 .302.116.58.351.833.233.254.577.458 1.03.613.453.156.992.234 1.615.234h2.938v1.812h-7.366zM29.833 109.129a3.33 3.33 0 0 1-1.432 1.169 4.535 4.535 0 0 1-1.805.373 4.537 4.537 0 0 1-1.807-.373c-.579-.248-1.057-.638-1.432-1.169s-.563-1.196-.563-1.995c0-.771.183-1.413.549-1.93a3.28 3.28 0 0 1 1.382-1.141 4.221 4.221 0 0 1 1.709-.364h.746v5.071c.447-.02.838-.183 1.168-.49.332-.307.498-.724.498-1.248 0-.41-.093-.754-.277-1.031-.186-.278-.473-.529-.863-.753l.542-1.462c.69.303 1.224.724 1.592 1.265.371.541.556 1.235.556 2.083 0 .799-.188 1.464-.563 1.995m-4.085-3.574c-.41.088-.746.261-1.009.52-.262.258-.395.61-.395 1.06 0 .428.137.784.409 1.067.272.282.604.458.994.525v-3.172zM29.833 100.878c-.375.531-.852.921-1.432 1.169a4.552 4.552 0 0 1-3.612 0c-.579-.248-1.057-.638-1.432-1.169s-.563-1.196-.563-1.995c0-.77.183-1.412.549-1.93a3.278 3.278 0 0 1 1.382-1.14 4.222 4.222 0 0 1 1.709-.365h.746v5.072a1.794 1.794 0 0 0 1.168-.49c.332-.307.498-.724.498-1.249 0-.41-.093-.753-.277-1.031-.186-.277-.473-.528-.863-.753l.542-1.462c.69.302 1.224.724 1.592 1.265.371.541.556 1.234.556 2.083 0 .799-.188 1.464-.563 1.995m-4.085-3.573c-.41.088-.746.261-1.009.519-.262.258-.395.611-.395 1.06 0 .429.137.784.409 1.067.272.282.604.458.994.526v-3.172zM35.481 16.926l-4.782 14.969h-3.266l-2.584-9.682-2.584 9.682h-3.268l-4.781-14.969h3.713l2.673 10.276 2.524-10.276h3.445l2.524 10.276 2.674-10.276zM37.979 27.083c1.426 0 2.495 1.068 2.495 2.495 0 1.425-1.069 2.495-2.495 2.495-1.425 0-2.495-1.07-2.495-2.495-.001-1.427 1.07-2.495 2.495-2.495" />
              </g>
            </svg>
          </a>
        </div>
      )}
    </div>
  );
};

export default CaseStudyPage;
